import { useEffect, useRef } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import debounce from '@/utils/debounce/debounce'
import websocket from '@/utils/websocket'
import {
  CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS,
  isIntegrationMessageDeletedWsMessage,
  isIntegrationMessageDeliveredWsMessage,
  isIntegrationMessageInsertedWsMessage,
  isIntegrationMessageNotDeliveredWsMessage,
  isIntegrationMessageReadWsMessage,
  matchesIntegrationMessageDeliveredContext,
  matchesIntegrationMessageNotDeliveredContext,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'

export type UseCrmIntegrationRealtimeMessagesParams = {
  leadId: number
  conversationId?: number | null
  provider?: CrmChatProviderKey
  enabled?: boolean
  loadMessages: (loadOptions?: { silent?: boolean }) => Promise<void>
  onIncomingMessage?: () => void
  /** Коллега / свой mark-read → локально unread conversation = 0 */
  onConversationRead?: () => void
  onOutgoingMessageDelivered?: (messageId: number) => void
  onOutgoingMessageNotDelivered?: (payload: {
    messageId: number
    error: string
  }) => void
  /** Удаление сообщения (своё или коллеги) → убрать из ленты + обновить last_message */
  onMessageDeleted?: (payload: {
    messageId: number
    conversationId: number
    lastMessageId: string | null
    lastMessageText: string | null
    unreadCount: number
  }) => void
}

/** WS integration_message_* → refetch ленты и подтверждение исходящих TG/MAX. */
export const useCrmIntegrationRealtimeMessages = ({
  leadId,
  conversationId = null,
  provider,
  enabled = true,
  loadMessages,
  onIncomingMessage,
  onConversationRead,
  onOutgoingMessageDelivered,
  onOutgoingMessageNotDelivered,
  onMessageDeleted,
}: UseCrmIntegrationRealtimeMessagesParams): void => {
  const leadIdRef = useRef(leadId)
  leadIdRef.current = leadId

  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  const providerRef = useRef(provider)
  providerRef.current = provider

  const loadMessagesRef = useRef(loadMessages)
  loadMessagesRef.current = loadMessages

  const onIncomingMessageRef = useRef(onIncomingMessage)
  onIncomingMessageRef.current = onIncomingMessage

  const onConversationReadRef = useRef(onConversationRead)
  onConversationReadRef.current = onConversationRead

  const onOutgoingMessageDeliveredRef = useRef(onOutgoingMessageDelivered)
  onOutgoingMessageDeliveredRef.current = onOutgoingMessageDelivered

  const onOutgoingMessageNotDeliveredRef = useRef(onOutgoingMessageNotDelivered)
  onOutgoingMessageNotDeliveredRef.current = onOutgoingMessageNotDelivered

  const onMessageDeletedRef = useRef(onMessageDeleted)
  onMessageDeletedRef.current = onMessageDeleted

  const debouncedSilentLoadRef = useRef(
    debounce(() => {
      void loadMessagesRef.current({ silent: true })
      onIncomingMessageRef.current?.()
    }, CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS),
  )

  const getDeliveryContext = () => ({
    leadId: leadIdRef.current,
    conversationId: conversationIdRef.current,
    provider: providerRef.current,
  })

  useEffect(() => {
    if (!enabled) {
      return
    }

    websocket.connect()

    const unsubscribeIntegrationInserted = websocket.subscribe(
      'integration_message_inserted',
      (message: unknown) => {
        if (!isIntegrationMessageInsertedWsMessage(message)) {
          return
        }

        const payload = message.data.payload
        if (payload.lead_id !== leadIdRef.current) {
          return
        }

        const activeConversationId = conversationIdRef.current
        if (
          activeConversationId != null &&
          Number(payload.conversation_id) !== Number(activeConversationId)
        ) {
          return
        }

        if (
          providerRef.current &&
          typeof payload.provider === 'string' &&
          payload.provider !== providerRef.current
        ) {
          return
        }

        debouncedSilentLoadRef.current()
      },
    )

    const unsubscribeIntegrationRead = websocket.subscribe(
      'integration_message_read',
      (message: unknown) => {
        if (!isIntegrationMessageReadWsMessage(message)) {
          return
        }

        const payload = message.data.payload
        if (Number(payload.lead_id) !== Number(leadIdRef.current)) {
          return
        }

        const activeConversationId = conversationIdRef.current
        if (
          activeConversationId == null ||
          Number(payload.conversation_id) !== Number(activeConversationId)
        ) {
          return
        }

        if (
          providerRef.current &&
          typeof payload.provider === 'string' &&
          payload.provider !== providerRef.current
        ) {
          return
        }

        onConversationReadRef.current?.()
      },
    )

    const unsubscribeDelivered = websocket.subscribe(
      'integration_message_delivered',
      (message: unknown) => {
        if (!isIntegrationMessageDeliveredWsMessage(message)) {
          return
        }

        if (!matchesIntegrationMessageDeliveredContext(message, getDeliveryContext())) {
          return
        }

        const messageId = Number(message.data.payload.message_id)
        onOutgoingMessageDeliveredRef.current?.(messageId)
        debouncedSilentLoadRef.current()
      },
    )

    const unsubscribeNotDelivered = websocket.subscribe(
      'integration_message_not_delivered',
      (message: unknown) => {
        if (!isIntegrationMessageNotDeliveredWsMessage(message)) {
          return
        }

        if (
          !matchesIntegrationMessageNotDeliveredContext(message, getDeliveryContext())
        ) {
          return
        }

        const { message_id: messageId, error } = message.data.payload
        onOutgoingMessageNotDeliveredRef.current?.({
          messageId: Number(messageId),
          error,
        })
        debouncedSilentLoadRef.current()
      },
    )

    const unsubscribeDeleted = websocket.subscribe(
      'integration_message_deleted',
      (message: unknown) => {
        if (!isIntegrationMessageDeletedWsMessage(message)) {
          return
        }

        const payload = message.data.payload
        if (Number(payload.lead_id) !== Number(leadIdRef.current)) {
          return
        }

        const activeConversationId = conversationIdRef.current
        if (
          activeConversationId == null ||
          Number(payload.conversation_id) !== Number(activeConversationId)
        ) {
          return
        }

        if (
          providerRef.current &&
          typeof payload.provider === 'string' &&
          payload.provider !== providerRef.current
        ) {
          return
        }

        onMessageDeletedRef.current?.({
          messageId: Number(payload.message_id),
          conversationId: Number(payload.conversation_id),
          lastMessageId:
            payload.last_message_id == null
              ? null
              : String(payload.last_message_id),
          lastMessageText:
            payload.last_message_text == null
              ? null
              : String(payload.last_message_text),
          unreadCount: Number(payload.unread_count) || 0,
        })
      },
    )

    return () => {
      unsubscribeIntegrationInserted?.()
      unsubscribeIntegrationRead?.()
      unsubscribeDelivered?.()
      unsubscribeNotDelivered?.()
      unsubscribeDeleted?.()
    }
  }, [conversationId, enabled, leadId, provider])
}
